import { execute } from '@tutorabc/webview-jsbridge'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useProfileData } from '../../hooks/useProfileData'
import { ProfileReducerActionTypesEnum, useProfileContext } from '../../reducer'

// Libs
import { useIsMemberFollowed } from '@/hooks/livemallclub/useFollow'
import useMemberVips from '@/hooks/livemallclub/useMemberVips'
import useQueryParamsNavigation from '@/hooks/livemallclub/useQueryParamsNavigation'
import useVipFollowers from '@/hooks/livemallclub/useVipFollowers'
import { useMemberLivesQuery } from '../../hooks/useMemberLivesQuery'
import useMemberProductsQuery from '../../hooks/useMemberProductsQuery'

// components
import Icon from '@/assets/icons'
import Image from '@/components/common/Image'
import Button from '@/components/common/livemallclub/Button'
import FollowActionButton from '@/components/shared/livemallclub/FollowActionButton'
import { BRIDGE_HANDLER } from '@/constants/bridgeHandler'
import BackToProfile from '../../sharedComponents/BackToProfile'
import FollowersOverview from './components/FollowersOverview'
import SocialInfo from './components/SocialInfo'

// constants
import { envConfigs } from '@/constants/config'
import { ROUTE_PATH } from '@/constants/routePath'
import { ROUTE_QUERY_STRING } from '@/constants/routeQueryString'
import { isClientSideShopApp } from '@/utils/navigator'
import { TAB_TYPE } from '../MemberItemList'

// Styles
import styles from './style.module.scss'
import useFollowParams from '@/hooks/useFollowerType'
import { USER_ROLE } from '@/types/user-profile'

const cx = classnames.bind(styles)

interface ICommunicationChannel {
  type: string
  data: string
}

export const COMMUNICATION_CHANNEL_TYPE = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  WEBSITE: 'Website',
  EMAIL: 'Email',
}

export const ProfileHeading = () => {
  const { t } = useTranslation()
  const {
    refetchFollowData,
    profileType,
    name,
    intro,
    followers,
    followingMembers,
    isMyProfile,
    isVendor,
    isKol,
    headshotUrl,
    coverPhotoUrl,
    isMemberProfile,
    roleInfo,
    merchantInfo,
    memberIdToQuery,
    merchantId,
  } = useProfileData()
  const { state, dispatch } = useProfileContext()
  const { isManageMode } = state
  const router = useRouter()
  const { query, locale } = router
  const isProfileNavigation = router.pathname === ROUTE_PATH.PROFILE_PAGE
  const isShowBackButton = isProfileNavigation && query.id
  const instagramUrl = roleInfo?.instagramUrl
  const facebookUrl = roleInfo?.facebookUrl
  const websiteUrl = merchantInfo?.websiteUrl
  const emailAddress = roleInfo?.email
  const hasSocialIcon = instagramUrl || facebookUrl || websiteUrl || emailAddress
  const followerType = useFollowParams()
  const { isMemberFollowed, isFetchMemberFollowedSuccess, refetchIsMemberFollowed } = useIsMemberFollowed({
    memberInfoId: memberIdToQuery,
    followeeType: followerType.role,
  })
  const { data: memberVipsData } = useMemberVips({ memberInfoId: memberIdToQuery, enabled: isMemberProfile && !!memberIdToQuery })
  const isVipMember = isMemberProfile && (memberVipsData?.length ?? 0) > 0

  const { query: roomListQuery } = useMemberLivesQuery({
    memberInfoId: memberIdToQuery,
    type: router.query.type === TAB_TYPE.LIVES ? 'live' : 'vip',
    enabled: (isManageMode && router.query.type === TAB_TYPE.LIVES) || (isManageMode && router.query.type === TAB_TYPE.VIP),
  })
  const { query: productListQuery } = useMemberProductsQuery({ memberInfoId: memberIdToQuery, enabled: isManageMode })
  const { data: vipFollowersData } = useVipFollowers({ merchantId: merchantInfo?.id })
  const { handleNavigation: handleQueryParamsNavigation } = useQueryParamsNavigation()
  const currentTab = router.query.type
  const refetchActionMap: Record<string, () => void> = {
    [TAB_TYPE.LIVES]: roomListQuery.refetch,
    [TAB_TYPE.SHOP]: productListQuery.refetch,
    [TAB_TYPE.VIP]: roomListQuery.refetch,
  }

  const handleFollowActionCallback = () => {
    refetchFollowData()
    refetchIsMemberFollowed()
  }
  const handleManageButtonClick = () => {
    if (isManageMode) {
      const refetchAction = refetchActionMap[String(currentTab)]
      refetchAction?.()
    }
    dispatch({ type: ProfileReducerActionTypesEnum.SET_MANAGE_MODE, payload: !isManageMode })
  }
  const handleEditPencilClick = () => {
    dispatch({ type: ProfileReducerActionTypesEnum.SET_EDIT_PROFILE_MODE, payload: true })
  }
  const handleMoreButtonClick = () => {
    if (!isClientSideShopApp) {
      handleQueryParamsNavigation('PROFILE_MORE_MENU_OPEN')
      return
    }
    execute({
      handlerName: BRIDGE_HANDLER.OPEN_WEB_VIEW,
      data: {
        url: `${envConfigs.domainUrl}/${locale}${ROUTE_PATH.PROFILE}?${new URLSearchParams(ROUTE_QUERY_STRING.PROFILE_MORE_MENU_OPEN)}`,
        showNavigation: false,
      },
    })
  }

  const handleBackClick = () => {
    router.back()
  }

  const handleCommunicationChannel = ({ type, data }: ICommunicationChannel) => {
    execute({
      handlerName: BRIDGE_HANDLER.OPEN_COUMMUNICATION_CHANNEL,
      data: {
        type,
        data,
      },
    })
  }
  const hasMoreButtons = isMyProfile && (isVendor || isKol)
  const showFollowButton = !hasMoreButtons && !isMyProfile
  const hasCover = (isVendor || isKol) && !!coverPhotoUrl
  const showUploadPlaceholder = !isMemberProfile && !hasCover
  const shouldShowIntro = !isMemberProfile
  const hasFollowers = followers.length > 0
  const isFollowersOverviewShow = !isMemberProfile && !isMyProfile && hasFollowers

  return (
    <div className={cx('profile-heading')} data-type={profileType}>
      <div className={cx('profile-heading-cover-wrapper')}>
        <div className={cx('profile-heading-cover', { placeholder: showUploadPlaceholder, isMemberProfile })}>
          {hasCover && (
            <>
              <Image
                src={coverPhotoUrl}
                alt='image'
                fill
                style={{
                  objectFit: 'cover',
                  objectPosition: 'top',
                }}
              />
              <div className={cx('profile-heading-cover-mask')} />
            </>
          )}
        </div>
        {isMyProfile && (
          <div className={cx('profile-heading-cover-pencil')} onClick={handleEditPencilClick}>
            <Icon.Pencil />
          </div>
        )}
        {isShowBackButton && <BackToProfile className={cx('back')} theme='white' onClick={handleBackClick} />}
      </div>
      <div className={cx('profile-heading-info')}>
        <div className={cx('profile-heading-info-heading')}>
          <div className={cx('profile-heading-info-heading-avatar')}>
            <div className={cx('profile-heading-info-heading-avatar-img', { isVip: isVipMember })}>
              {headshotUrl ? (
                <Image
                  src={headshotUrl}
                  alt='image'
                  fill
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                />
              ) : (
                <Icon.DefaultHeadShot />
              )}
            </div>
            {isVipMember && (
              <div className={cx('profile-heading-info-heading-avatar-vip-tag')}>
                <Icon.Diamond /> <span className={cx('profile-heading-info-heading-avatar-vip-tag-text')}>VIP</span>
              </div>
            )}
          </div>
          <div className={cx('profile-heading-info-heading-block')}>
            <div className={cx('profile-heading-info-heading-block-name')}>{name}</div>
            {hasSocialIcon && (
              <div className={cx('profile-heading-info-heading-block-social')}>
                {instagramUrl && (
                  <div
                    onClick={() =>
                      handleCommunicationChannel({
                        type: COMMUNICATION_CHANNEL_TYPE.INSTAGRAM,
                        data: instagramUrl,
                      })
                    }
                    className={cx('profile-heading-info-heading-block-social-icon', 'instagram')}
                  >
                    <Icon.Instagram />
                  </div>
                )}
                {facebookUrl && (
                  <div
                    onClick={() =>
                      handleCommunicationChannel({
                        type: COMMUNICATION_CHANNEL_TYPE.FACEBOOK,
                        data: facebookUrl,
                      })
                    }
                    className={cx('profile-heading-info-heading-block-social-icon', 'facebook')}
                  >
                    {<Icon.SocialFacebook />}
                  </div>
                )}
                {websiteUrl && (
                  <div
                    onClick={() => handleCommunicationChannel({ type: COMMUNICATION_CHANNEL_TYPE.WEBSITE, data: websiteUrl })}
                    className={cx('profile-heading-info-heading-block-social-icon', 'website')}
                  >
                    {<Icon.SocialWebsite />}
                  </div>
                )}
                {emailAddress && (
                  <div
                    onClick={() => handleCommunicationChannel({ type: COMMUNICATION_CHANNEL_TYPE.EMAIL, data: emailAddress })}
                    className={cx('profile-heading-info-heading-block-social-icon', 'website')}
                  >
                    {<Icon.L_Email />}
                  </div>
                )}
              </div>
            )}
            {isMemberProfile && <SocialInfo followers={followers} followingMembers={followingMembers} vipClubMembers={memberVipsData ?? []} />}
          </div>
        </div>
        {!isMemberProfile && (
          <SocialInfo followers={followers} followingMembers={followingMembers} vipClubMembers={vipFollowersData?.memberInfo ?? []} />
        )}
        {isFollowersOverviewShow && <FollowersOverview followers={followers} />}

        {shouldShowIntro && <div className={cx('profile-heading-info-description')}>{intro || t('Common_Edit_Bio')}</div>}
        {hasMoreButtons && (
          <div className={cx('profile-heading-info-buttons')}>
            <Button theme={isManageMode ? 'pink' : 'white'} onClick={handleManageButtonClick}>
              {isManageMode ? t('Common_Done') : t('Common_Manage')}
            </Button>
            <Button theme='white' onClick={handleMoreButtonClick}>
              {t('Common_More')}
            </Button>
          </div>
        )}
        {showFollowButton && isFetchMemberFollowedSuccess && (
          <FollowActionButton
            mutationCallBack={handleFollowActionCallback}
            memberInfoId={isVendor ? merchantId : memberIdToQuery}
            isFollowing={isMemberFollowed}
            isThemChangeByState={false}
            className={cx('follow-button')}
            followerType={followerType.role}
            followeeType={isVendor ? USER_ROLE.MERCHANT : USER_ROLE.KOL}
          />
        )}
      </div>
    </div>
  )
}
